// ---------------------------------------------------------------------------
// Accessify – Text Simplification Feature (Safe Replace Engine)
// ---------------------------------------------------------------------------
// Phase 1: Manifest-based precomputed simplification
//   1. Fetch manifest from /v1/manifest for current page
//   2. Apply cached variants instantly via leaf-node replacement
//   3. Live-fallback only for blocks not in manifest
// Phase 2: Layout-safe replacement with guards + observers
// ---------------------------------------------------------------------------

import type { FeatureModule, FeatureState, AIService } from '../types';
import {
  tokenize,
  tokenSimilarity,
  STALE_SIMILARITY_THRESHOLD,
  isObviousBoilerplate,
} from './text-simplify-helpers';
import { isInStylePass } from './style-pass-guard';
import type { SimplifyLevel, ResimplifyMode } from './text-simplify-types';
import { createDiagger, type DiagBlock } from './text-simplify-diag';
import { createIdbCache } from './text-simplify-idb';
import { createTelemetry } from './text-simplify-telemetry';
import { pageLocation } from '../preview-url';

// ─── Persistent site-mode cache ───────────────────────────────────────────────
// When the manifest fetch fails (worker outage, network blip), we fall back to
// the last successfully retrieved siteMode for this siteKey, with a 7-day TTL.
// Without this, a transient outage on the *first* page-load of a session would
// pin `currentSiteMode` to the safe-but-wrong `manual` default and silently
// disable live AI on an `auto`-mode site.
const SITE_MODE_CACHE_KEY_PREFIX = 'accessify-site-mode:';
const SITE_MODE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function loadCachedSiteMode(siteKey: string | undefined): ResimplifyMode {
  if (!siteKey || typeof localStorage === 'undefined') return 'manual';
  try {
    const raw = localStorage.getItem(SITE_MODE_CACHE_KEY_PREFIX + siteKey);
    if (!raw) return 'manual';
    const { mode, ts } = JSON.parse(raw) as { mode: string; ts: number };
    if (typeof ts !== 'number' || Date.now() - ts > SITE_MODE_CACHE_TTL_MS) return 'manual';
    return mode === 'auto' ? 'auto' : 'manual';
  } catch {
    return 'manual';
  }
}

function persistSiteMode(siteKey: string | undefined, mode: ResimplifyMode): void {
  if (!siteKey || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      SITE_MODE_CACHE_KEY_PREFIX + siteKey,
      JSON.stringify({ mode, ts: Date.now() }),
    );
  } catch {
    // QuotaExceeded / disabled storage — best-effort, no user-facing impact.
  }
}

interface TextSimplifyState {
  level: SimplifyLevel;
}

interface SavedTextNode {
  node: Text;
  original: string;
}

interface SavedParagraph {
  el: HTMLElement;
  originalHtml: string;
  savedTextNodes: SavedTextNode[];
  ancestorPatches: AncestorPatch[];
}

interface AncestorPatch {
  el: HTMLElement;
  property: string;
  originalValue: string;
}

interface ManifestBlock {
  selector: string;
  blockHash: string;
  result: string;
  originalText?: string;  // For staleness check on dynamic pages
}

interface RuntimeManifest {
  url: string;
  siteKey: string;
  feature: string;
  variant: string;
  blocks: ManifestBlock[];
  generatedAt: string;
  contentHash: string;
  siteMode?: ResimplifyMode; // undefined = treated as manual (safe default)
  /** Per-site opt-in for pagehide telemetry. Widget only fires the beacon
   *  when this is explicitly true. Absent/false = no beacon, zero overhead. */
  telemetryEnabled?: boolean;
}

export default function createTextSimplifyModule(
  aiService?: AIService,
  lang: string = 'de',
  options?: { siteKey?: string; proxyUrl?: string; simplificationLevel?: SimplifyLevel } | SimplifyLevel,
): FeatureModule {
  let enabled = false;
  const defaultLevel = typeof options === 'string' ? options
    : (options as any)?.simplificationLevel || undefined;
  let level: SimplifyLevel = defaultLevel || 'einfache';
  // siteKey known at construction time (may also be picked up from data-attrs
  // later via getConfig()); used to seed `currentSiteMode` from the persistent
  // cache so the FIRST page-load in a session does not silently downgrade an
  // `auto`-mode site to `manual` while the manifest request is still in flight
  // or if it fails outright.
  const constructorSiteKey = typeof options === 'object' && options !== null
    ? (options as { siteKey?: string }).siteKey
    : undefined;
  let savedParagraphs: SavedParagraph[] = [];
  let abortController: AbortController | null = null;
  let overlayEl: HTMLDivElement | null = null;
  let skippedBlocks = 0;

  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;
  // Current site's re-simplify mode. Seeded from localStorage cache (7-day TTL)
  // so a transient worker outage does not pin auto-mode sites to the safe
  // `manual` default. Refreshed from every successful manifest response.
  // 'manual' (default) = never call live AI for unknown blocks.
  // 'auto' = live AI fallback + D1 auto-persist is allowed.
  let currentSiteMode: ResimplifyMode = loadCachedSiteMode(constructorSiteKey);
  // Per-site telemetry opt-in. Set from manifest response.
  let telemetryEnabled = false;

  // ─── Re-mount recovery map ──────────────────────────────────────────────────
  // dataset.accessifyOriginal lives on the DOM node. Reactive frameworks
  // (Framer, React) re-mount subtrees on style changes (e.g. when text-size
  // sets font-size), wiping every data-* attribute. Without this map the
  // next simplify pass would treat the *already simplified* textContent as
  // the new "original", hash it, miss the manifest, and call the API again.
  //
  // Selector keys are tag + nth-of-type position; they survive re-mounts
  // because the framework rebuilds the same DOM shape, just with fresh node
  // identities. WeakRef would not help — the old node is GC'd.
  interface RemountRecord {
    original: string;
    simplified: string;
    blockHash: string;
  }
  const originalBySelector = new Map<string, RemountRecord>();

  /** Build a stable, framework-resilient selector for a node.
   *  Tag + nth-of-type chain, capped at 8 levels deep to keep keys short. */
  function stableSelector(el: HTMLElement): string {
    const path: string[] = [];
    let node: HTMLElement | null = el;
    while (node && node !== document.body && path.length < 8) {
      const parent: HTMLElement | null = node.parentElement;
      if (!parent) break;
      const sameTag = Array.from(parent.children).filter(
        (c) => c.tagName === node!.tagName,
      );
      const idx = sameTag.indexOf(node);
      path.unshift(`${node.tagName.toLowerCase()}:nth-of-type(${idx + 1})`);
      node = parent;
    }
    return path.join('>');
  }

  // Pilot telemetry — extracted to text-simplify-telemetry.ts. Numeric-only,
  // no PII, beaconed on pagehide. `tel.run` is null between runs and the
  // .start(...) call seeds a fresh object at the top of each simplifyPage().
  const tel = createTelemetry(() => telemetryEnabled, hashText);

  // Map from element to pre-replacement dimensions for resize revert checks
  const preReplaceDimensions = new Map<HTMLElement, { scrollHeight: number; clientHeight: number }>();

  const STORAGE_KEY = 'accessify-text-simplify';
  const SIMPLIFIED_ATTR = 'data-accessify-simplified';
  const ORIGINAL_ATTR = 'data-accessify-original';
  const BLOCK_HASH_ATTR = 'data-accessify-block-hash';

  // Diagnostic logger — extracted to text-simplify-diag.ts. The Diagger
  // instance owns its own currentRun/history closure; tree-shakes out of
  // production builds via the __ACCESSIFY_DIAG_BUILD__ vite-define.
  const dx = createDiagger(level, () => currentSiteMode);

  // IndexedDB cache for instant reload — extracted to text-simplify-idb.ts.
  // The factory binds `level` so callers don't have to thread it through
  // every key/get/set call.
  const idb = createIdbCache(level);

  // -------------------------------------------------------------------------
  // Resolve config from the script tag or global config
  // -------------------------------------------------------------------------

  function getConfig(): { siteKey?: string; proxyUrl?: string } {
    const script = document.querySelector('script[data-site-key]');
    const siteKey = script?.getAttribute('data-site-key') ||
      (window as any).Accessify?.config?.siteKey || '';
    const proxyUrl = script?.getAttribute('data-proxy-url') ||
      (window as any).Accessify?.config?.proxyUrl || '';
    return { siteKey: siteKey || undefined, proxyUrl: proxyUrl || undefined };
  }

  // -------------------------------------------------------------------------
  // Find text-heavy elements on the page
  // -------------------------------------------------------------------------

  /**
   * FAQ/Accordion safety: these containers must NOT be treated as a single
   * simplify block. Instead, we descend into their children (question vs answer).
   */
  // FAQ / Accordion / Disclosure containers — must NEVER be simplified as one block.
  // Covers: native <details>, ARIA patterns, common class-name conventions,
  // component libraries (Radix, Headless UI, MUI, Bootstrap), and generic
  // collapsible widgets. Matched via .closest() so only the *container* is detected.
  const FAQ_CONTAINER_SELECTORS = [
    'details',
    '[role="region"][aria-labelledby]',
    '[aria-expanded]',
    '[data-state="open"]',
    '[data-state="closed"]',
    '[class*="accordion" i]',
    '[class*="faq" i]',
    '[class*="disclosure" i]',
    '[class*="expandable" i]',
    '[class*="collapsible" i]',
    '[class*="collapse" i]',
  ].join(', ');
  const FAQ_SKIP_SELECTORS = 'summary, [role="button"], .accordion-header, .accordion__header, .faq-question';

  /** Lines that are photo credits, attributions, or metadata — not actual content.
   *  These should NOT be simplified (risk of hallucination on short context). */
  const CREDIT_LINE_PATTERN = /^(Bild|Foto|Photo|Image|Fotocredit|Credit|Text|Autor|Author|Quelle|Source|Copyright|©|\(c\))\s*[:·\-–—]/i;

  function getTextElements(): HTMLElement[] {
    const selectors = 'h1, h2, h3, h4, h5, h6, p, li, td, th, blockquote, figcaption, .hero-subtitle, summary, dt, dd';
    const elements: HTMLElement[] = [];
    const seen = new Set<HTMLElement>();

    // Scan the entire page — the manifest acts as the real filter.
    // Only blocks whose hash matches a manifest entry get replaced;
    // everything else (nav, footer, sidebar) is skipped automatically.
    // This avoids brittle content-root heuristics that break on Framer,
    // Webflow, Shopify, and other non-standard DOM structures.
    document.body.querySelectorAll(selectors).forEach((el) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.closest('#accessify-root')) return;
      // Skip truly hidden elements, but allow Framer scroll-animated ones
      // (they have offsetParent===null due to opacity:0 / will-change but ARE real content)
      if (htmlEl.offsetParent === null && htmlEl.style.position !== 'fixed') {
        const isFramerAnimated = htmlEl.closest('[data-framer-appear-id]') || htmlEl.closest('[data-framer-name]');
        if (!isFramerAnimated) return;
      }
      // Freeze the original text on first encounter — this ensures we always
      // hash the real page text, never text that was already replaced by us.
      // This prevents the "simplified text stored as original" corruption loop.
      if (!htmlEl.dataset.accessifyOriginal) {
        // Re-mount recovery: frameworks (Framer / React) wipe data-attributes
        // when they re-mount a subtree. If we have a record of this selector
        // and the current text matches the simplified value we wrote earlier,
        // restore the dataset attribute and skip — the block is already
        // correct; touching it again would re-hash the simplified text and
        // burn another API call.
        const sel = stableSelector(htmlEl);
        const known = originalBySelector.get(sel);
        if (known) {
          const currentText = (htmlEl.textContent || '').trim();
          if (currentText === known.simplified.trim()) {
            // Element is re-mounted but already shows the simplified text.
            htmlEl.dataset.accessifyOriginal = known.original;
            htmlEl.setAttribute(SIMPLIFIED_ATTR, 'true');
            htmlEl.setAttribute(BLOCK_HASH_ATTR, known.blockHash);
            return; // skip — already correct
          }
          if (currentText === known.original.trim()) {
            // Re-mounted, framework reverted the visible text to the original.
            // Restore dataset so the normal flow rehashes the same block and
            // can hit the cache (manifest / IDB) instead of the API.
            htmlEl.dataset.accessifyOriginal = known.original;
          } else {
            // Text drift (page actually changed): treat as a fresh block.
            htmlEl.dataset.accessifyOriginal = htmlEl.textContent?.trim() || '';
          }
        } else {
          htmlEl.dataset.accessifyOriginal = htmlEl.textContent?.trim() || '';
        }
      }
      const text = htmlEl.dataset.accessifyOriginal;
      // Length cutoff: headings can be content-meaningful at 10ch
      // ("Kontakt", "Über uns", "Haftung Für Inhalte") while paragraph/list
      // fragments below 20ch are mostly UI scraps ("Mehr lesen", "Login")
      // that aren't worth simplifying. Without the heading carve-out, every
      // short Impressum/Disclaimer subheading was getting skipped before the
      // manifest could even match it (rhinos impressum blocks #5/#8).
      const isHeading = /^H[1-6]$/.test(htmlEl.tagName);
      const minLen = isHeading ? 10 : 20;
      if (text.length < minLen) return;
      // Skip short photo credits / attribution lines (high hallucination risk)
      if (text.length < 60 && CREDIT_LINE_PATTERN.test(text)) return;
      if (seen.has(htmlEl)) return;

      // FAQ safety: skip elements whose parent is an FAQ container if the
      // element itself is a question/toggle (those are handled separately)
      // This prevents question+answer from being merged into one block.
      const inFaqContainer = htmlEl.closest(FAQ_CONTAINER_SELECTORS);
      if (inFaqContainer) {
        // Inside an FAQ container: only collect leaf text elements,
        // not the container itself. Skip if this element contains
        // both question and answer content (too risky to simplify as one).
        const childBlocks = htmlEl.querySelectorAll('p, li, dd, dt, summary');
        if (childBlocks.length > 1) {
          // This element wraps multiple sub-blocks — skip it, let children be collected
          if ((window as any).__ACCESSIFY_DEBUG) {
            console.log('[Accessify] FAQ: skipping wrapper, will collect children:', htmlEl.tagName);
          }
          return;
        }
      }

      let dominated = false;
      for (const s of seen) {
        if (s.contains(htmlEl)) { dominated = true; break; }
      }
      if (dominated) return;
      seen.add(htmlEl);
      elements.push(htmlEl);
    });

    return elements;
  }

  // -------------------------------------------------------------------------
  // Block hash — DJB2, same as server side
  // -------------------------------------------------------------------------

  function hashText(text: string): string {
    const normalized = text.replace(/\s+/g, ' ').trim().toLowerCase();
    let hash = 5381;
    for (let i = 0; i < normalized.length; i++) {
      hash = ((hash << 5) + hash + normalized.charCodeAt(i)) & 0x7fffffff;
    }
    return hash.toString(36);
  }

  // -------------------------------------------------------------------------
  // Staleness check — compare current page text with DB original
  // -------------------------------------------------------------------------

  /**
   * Fast character-diff count between two normalized strings.
   * Uses Levenshtein distance for short strings, falls back to
   * a simple diff-count for longer texts (performance).
   */
  function charDiff(a: string, b: string): number {
    const na = a.replace(/\s+/g, ' ').trim();
    const nb = b.replace(/\s+/g, ' ').trim();

    // Fast path: identical
    if (na === nb) return 0;

    // For very long strings, use a quick heuristic (length diff + sample comparison)
    if (na.length > 500 || nb.length > 500) {
      const lenDiff = Math.abs(na.length - nb.length);
      if (lenDiff > 3) return lenDiff;
      // Compare char-by-char up to the shorter length
      let diffs = lenDiff;
      const minLen = Math.min(na.length, nb.length);
      for (let i = 0; i < minLen; i++) {
        if (na[i] !== nb[i]) diffs++;
        if (diffs > 3) return diffs; // early exit
      }
      return diffs;
    }

    // Levenshtein distance for shorter strings (accurate)
    const m = na.length;
    const n = nb.length;
    // Early exit if length diff alone exceeds threshold
    if (Math.abs(m - n) > 3) return Math.abs(m - n);

    const dp: number[] = Array.from({ length: n + 1 }, (_, i) => i);
    for (let i = 1; i <= m; i++) {
      let prev = dp[0];
      dp[0] = i;
      for (let j = 1; j <= n; j++) {
        const temp = dp[j];
        dp[j] = na[i - 1] === nb[j - 1]
          ? prev
          : 1 + Math.min(prev, dp[j], dp[j - 1]);
        prev = temp;
      }
      // Early exit if minimum possible distance exceeds threshold
      if (Math.min(...dp) > 3) return 4;
    }
    return dp[n];
  }

  const STALENESS_THRESHOLD = 3; // max char-diff to still accept cached version

  // -------------------------------------------------------------------------
  // Leaf text node replacement — preserves inline markup
  // -------------------------------------------------------------------------

  function getLeafTextNodes(root: Node): Text[] {
    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let node: Text | null;
    while ((node = walker.nextNode() as Text | null)) {
      const trimmed = node.nodeValue?.trim();
      if (trimmed && trimmed.length > 0) {
        textNodes.push(node);
      }
    }
    return textNodes;
  }

  /**
   * Replace leaf text nodes within an element with simplified text.
   * The simplified text is distributed across existing text nodes proportionally,
   * preserving the surrounding inline markup structure.
   *
   * Strategy: if there is only one meaningful text node, replace it directly.
   * If there are multiple text nodes, split the simplified text into sentences
   * and distribute them proportionally across the existing text nodes.
   */
  function replaceLeafTextNodes(el: HTMLElement, simplifiedText: string): void {
    const textNodes = getLeafTextNodes(el);
    if (textNodes.length === 0) return;

    if (textNodes.length === 1) {
      // Single text node — straightforward replacement
      textNodes[0].nodeValue = simplifiedText;
      return;
    }

    // Multiple text nodes: distribute simplified text proportionally
    // Split simplified text into segments by sentence boundaries
    const sentences = simplifiedText.match(/[^.!?]+[.!?]*\s*/g) || [simplifiedText];

    // Calculate original character weight per text node
    const totalOriginalLen = textNodes.reduce((sum, n) => sum + (n.nodeValue?.trim().length || 0), 0);

    if (totalOriginalLen === 0) {
      // Fallback: put all simplified text in first node, clear others
      textNodes[0].nodeValue = simplifiedText;
      for (let i = 1; i < textNodes.length; i++) {
        textNodes[i].nodeValue = '';
      }
      return;
    }

    // Distribute sentences across text nodes based on character weight
    let sentenceIndex = 0;
    for (let i = 0; i < textNodes.length; i++) {
      const nodeWeight = (textNodes[i].nodeValue?.trim().length || 0) / totalOriginalLen;
      const sentenceCount = Math.max(1, Math.round(nodeWeight * sentences.length));

      const chunk = sentences.slice(sentenceIndex, sentenceIndex + sentenceCount).join('');
      textNodes[i].nodeValue = chunk || '';
      sentenceIndex += sentenceCount;
    }

    // If there are leftover sentences, append to the last non-empty node
    if (sentenceIndex < sentences.length) {
      const lastNode = textNodes[textNodes.length - 1];
      lastNode.nodeValue = (lastNode.nodeValue || '') + sentences.slice(sentenceIndex).join('');
    }
  }

  // -------------------------------------------------------------------------
  // Layout Guard — detect overflow/clipping after replacement
  // -------------------------------------------------------------------------

  function measureElement(el: HTMLElement): { scrollHeight: number; clientHeight: number } {
    return { scrollHeight: el.scrollHeight, clientHeight: el.clientHeight };
  }

  /**
   * Check if an element or its ancestors have overflow:hidden with fixed height
   * that would clip expanded content.
   */
  function findClippingAncestor(el: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = el;
    while (current && current !== document.body) {
      const style = getComputedStyle(current);
      const overflow = style.overflow + ' ' + style.overflowY;
      const hasHiddenOverflow = overflow.includes('hidden') || overflow.includes('clip');
      const hasFixedHeight = style.maxHeight !== 'none' || (
        style.height !== 'auto' && style.height !== '' && !style.height.includes('%')
      );
      if (hasHiddenOverflow && hasFixedHeight) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  /**
   * Check layout safety after a replacement. Returns true if safe, false if clipping detected.
   * Attempts minimal ancestor patches to resolve clipping before giving up.
   */
  function checkLayoutSafety(el: HTMLElement): { safe: boolean; patches: AncestorPatch[] } {
    const patches: AncestorPatch[] = [];

    // Check the element itself for overflow
    const after = measureElement(el);
    const isElementOverflowing = after.scrollHeight > after.clientHeight + 2; // 2px tolerance

    if (!isElementOverflowing) {
      // Also check for clipping ancestors
      const clipAncestor = findClippingAncestor(el);
      if (!clipAncestor) return { safe: true, patches: [] };

      const ancestorAfter = measureElement(clipAncestor);
      if (ancestorAfter.scrollHeight <= ancestorAfter.clientHeight + 2) {
        return { safe: true, patches: [] };
      }

      // Try patching the ancestor
      return tryAncestorPatches(clipAncestor, patches);
    }

    // Element itself is overflowing — check if a parent is clipping it
    const clipAncestor = findClippingAncestor(el);
    if (!clipAncestor) {
      // No clipping ancestor — overflow is fine (content just grows naturally)
      return { safe: true, patches: [] };
    }

    return tryAncestorPatches(clipAncestor, patches);
  }

  function tryAncestorPatches(
    ancestor: HTMLElement,
    patches: AncestorPatch[],
  ): { safe: boolean; patches: AncestorPatch[] } {
    const style = getComputedStyle(ancestor);

    // Try removing max-height
    if (style.maxHeight !== 'none') {
      patches.push({
        el: ancestor,
        property: 'maxHeight',
        originalValue: ancestor.style.maxHeight,
      });
      ancestor.style.maxHeight = 'none';
    }

    // Try setting overflow to visible
    if (style.overflow.includes('hidden') || style.overflowY.includes('hidden')) {
      patches.push({
        el: ancestor,
        property: 'overflow',
        originalValue: ancestor.style.overflow,
      });
      ancestor.style.overflow = 'visible';
    }

    // Re-check after patches
    const afterPatch = measureElement(ancestor);
    if (afterPatch.scrollHeight <= afterPatch.clientHeight + 2) {
      return { safe: true, patches };
    }

    // Patches didn't fix it — revert them
    revertPatches(patches);
    return { safe: false, patches: [] };
  }

  function revertPatches(patches: AncestorPatch[]) {
    for (const patch of patches) {
      (patch.el.style as any)[patch.property] = patch.originalValue;
    }
  }

  // -------------------------------------------------------------------------
  // Observers — ResizeObserver + MutationObserver
  // -------------------------------------------------------------------------

  function setupResizeObserver() {
    if (resizeObserver) return;

    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const el = entry.target as HTMLElement;
        if (!el.hasAttribute(SIMPLIFIED_ATTR)) continue;

        const saved = savedParagraphs.find((sp) => sp.el === el);
        if (!saved) continue;

        // Check if a layout change caused clipping
        const clipAncestor = findClippingAncestor(el);
        if (clipAncestor) {
          const dims = measureElement(clipAncestor);
          if (dims.scrollHeight > dims.clientHeight + 2) {
            // External layout change caused clipping — revert this block
            console.warn(
              '[Accessify] ResizeObserver detected clipping after external layout change, reverting block:',
              el,
            );
            revertSingleBlock(saved);
            skippedBlocks++;
          }
        }
      }
    });
  }

  function setupMutationObserver() {
    if (mutationObserver) return;

    mutationObserver = new MutationObserver((mutations) => {
      // Style-pass guard: text-size is mid-update (it sets inline font-size on
      // dozens of elements at once, frameworks then re-render the subtree).
      // Without this guard the observer would treat the re-render as an
      // "external mutation" and drop our simplified blocks from tracking,
      // causing the next pass to re-hash + re-call the API for content that
      // is already simplified.
      if (isInStylePass()) return;

      for (const mutation of mutations) {
        // Check if any removed nodes were tracked simplified blocks
        for (const removed of mutation.removedNodes) {
          if (!(removed instanceof HTMLElement)) continue;
          const idx = savedParagraphs.findIndex((sp) => sp.el === removed || removed.contains(sp.el));
          if (idx !== -1) {
            const saved = savedParagraphs[idx];
            // Element was removed from DOM by the page itself — clean up tracking
            resizeObserver?.unobserve(saved.el);
            saved.el.removeAttribute(SIMPLIFIED_ATTR);
            saved.el.removeAttribute(ORIGINAL_ATTR);
            saved.el.removeAttribute(BLOCK_HASH_ATTR);
            if (saved.el.dataset.accessifyOrigTransform) {
              saved.el.style.textTransform = saved.el.dataset.accessifyOrigTransform;
              delete saved.el.dataset.accessifyOrigTransform;
            }
            revertPatches(saved.ancestorPatches);
            savedParagraphs.splice(idx, 1);
          }
        }

        // Check if a simplified element's content was changed externally
        if (mutation.type === 'characterData' || mutation.type === 'childList') {
          const target = mutation.target instanceof HTMLElement
            ? mutation.target
            : mutation.target.parentElement;
          if (!target) continue;

          const simplifiedEl = target.closest(`[${SIMPLIFIED_ATTR}]`) as HTMLElement | null;
          if (simplifiedEl) {
            const saved = savedParagraphs.find((sp) => sp.el === simplifiedEl);
            if (saved && !simplifiedEl.hasAttribute('data-accessify-replacing')) {
              // External mutation on a simplified block — clean up
              console.warn(
                '[Accessify] External mutation detected on simplified block, cleaning up tracking:',
                simplifiedEl,
              );
              resizeObserver?.unobserve(saved.el);
              saved.el.removeAttribute(SIMPLIFIED_ATTR);
              saved.el.removeAttribute(ORIGINAL_ATTR);
              saved.el.removeAttribute(BLOCK_HASH_ATTR);
              if (saved.el.dataset.accessifyOrigTransform) {
                saved.el.style.textTransform = saved.el.dataset.accessifyOrigTransform;
                delete saved.el.dataset.accessifyOrigTransform;
              }
              revertPatches(saved.ancestorPatches);
              savedParagraphs = savedParagraphs.filter((sp) => sp.el !== simplifiedEl);
            }
          }
        }
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  function disconnectObservers() {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }
    preReplaceDimensions.clear();
  }

  // -------------------------------------------------------------------------
  // Safe replacement — leaf nodes + layout guard
  // -------------------------------------------------------------------------

  function safeReplace(
    el: HTMLElement,
    simplifiedText: string,
    blockHash?: string,
  ): boolean {
    // No-op guard: if the simplified text equals the current rendered text
    // there is nothing to do, and running replaceLeafTextNodes anyway would
    // redistribute the same characters across the leaf text nodes — which
    // for multi-text-node elements (anything with <br> + <a> like the
    // impressum Mobil/E-Mail line) corrupts inline structure character-by-
    // character and, after a few toggle-on/off cycles, leaves the DOM with
    // visible HTML-escaped markup. Catches both pass-through manifest
    // entries and rare LLM no-ops in one place.
    const currentText = (el.textContent || '').trim();
    if (simplifiedText.trim() === currentText) {
      if (blockHash) el.setAttribute(BLOCK_HASH_ATTR, blockHash);
      if (!el.dataset.accessifyOriginal) el.dataset.accessifyOriginal = currentText;
      el.setAttribute(SIMPLIFIED_ATTR, 'true');
      return true;
    }

    // Save original — both innerHTML (fallback) and individual text nodes (preferred)
    const originalHtml = el.innerHTML;
    const textNodesBefore = getLeafTextNodes(el);
    const savedTextNodes: SavedTextNode[] = textNodesBefore.map(n => ({
      node: n,
      original: n.data,
    }));

    // Guard flag to prevent MutationObserver from treating our own replacement as external
    el.setAttribute('data-accessify-replacing', 'true');

    // Perform leaf text node replacement (preserves inline markup)
    // ONLY sets textNode.nodeValue — never modifies DOM structure
    replaceLeafTextNodes(el, simplifiedText);

    // Check layout safety
    const { safe, patches } = checkLayoutSafety(el);

    if (!safe) {
      // Revert — restore via text node .data (React/Framer-safe)
      for (const saved of savedTextNodes) {
        if (saved.node.parentNode) saved.node.data = saved.original;
      }
      setTimeout(() => el.removeAttribute('data-accessify-replacing'), 0);
      console.warn(
        '[Accessify] Block skipped — layout clipping detected after replacement:',
        el.textContent?.slice(0, 60),
      );
      skippedBlocks++;
      return false;
    }

    // Replacement is safe — finalize
    el.setAttribute(SIMPLIFIED_ATTR, 'true');
    el.setAttribute(ORIGINAL_ATTR, originalHtml);
    if (blockHash) {
      el.setAttribute(BLOCK_HASH_ATTR, blockHash);
    }

    // Reset CSS text-transform so simplified text displays as-authored
    // (many sites apply uppercase/capitalize to headings/buttons)
    // Use setProperty with !important to override site CSS that uses !important
    const computed = window.getComputedStyle(el);
    if (computed.textTransform && computed.textTransform !== 'none') {
      el.dataset.accessifyOrigTransform = computed.textTransform;
      el.style.setProperty('text-transform', 'none', 'important');
    }

    savedParagraphs.push({ el, originalHtml, savedTextNodes, ancestorPatches: patches });

    // Track this block in the selector map so a framework re-mount can restore
    // identity without going back to the API. The `original` we record here is
    // the value frozen earlier in getTextElements() — it survives even when
    // dataset.accessifyOriginal gets wiped by the re-mount.
    if (blockHash) {
      const original = el.dataset.accessifyOriginal || '';
      if (original) {
        originalBySelector.set(stableSelector(el), {
          original,
          simplified: simplifiedText,
          blockHash,
        });
      }
    }

    // Observe for future layout changes
    resizeObserver?.observe(el);

    // Remove guard flag AFTER MutationObserver has processed current mutations
    setTimeout(() => el.removeAttribute('data-accessify-replacing'), 0);

    return true;
  }

  /**
   * Revert a single block — used by ResizeObserver when external layout changes cause clipping.
   */
  function revertSingleBlock(saved: SavedParagraph) {
    // Prefer text-node restore (React/Framer-safe: no DOM structure change)
    if (saved.savedTextNodes.length > 0) {
      for (const sn of saved.savedTextNodes) {
        if (sn.node.parentNode) sn.node.data = sn.original;
      }
    } else {
      // Fallback to innerHTML if text nodes are gone (framework re-rendered)
      saved.el.innerHTML = saved.originalHtml;
    }
    saved.el.removeAttribute(SIMPLIFIED_ATTR);
    saved.el.removeAttribute(ORIGINAL_ATTR);
    saved.el.removeAttribute(BLOCK_HASH_ATTR);
    // Restore original text-transform if we overrode it
    if (saved.el.dataset.accessifyOrigTransform) {
      saved.el.style.textTransform = saved.el.dataset.accessifyOrigTransform;
      delete saved.el.dataset.accessifyOrigTransform;
    }
    revertPatches(saved.ancestorPatches);
    resizeObserver?.unobserve(saved.el);
    savedParagraphs = savedParagraphs.filter((sp) => sp.el !== saved.el);
  }

  // -------------------------------------------------------------------------
  // Fetch manifest from API
  // -------------------------------------------------------------------------

  // In-memory manifest cache to avoid refetching on toggle
  const manifestCache = new Map<string, RuntimeManifest | null>();

  /** Hard upper bound for manifest fetch. Beyond this the run continues with
   *  no manifest instead of hanging the toggle flow. */
  const MANIFEST_FETCH_TIMEOUT_MS = 8000;

  /** Drop the in-memory manifest snapshot for the current page. Called after
   *  a successful persist so that the NEXT fetchManifest() reads through to
   *  the (now-fresh) KV manifest instead of returning the stale snapshot. */
  function invalidateManifestCacheForCurrentPage(siteKey: string): void {
    const cacheKey = `${siteKey}:${level}:${pageLocation().pathname}`;
    manifestCache.delete(cacheKey);
  }

  async function fetchManifest(
    siteKey: string,
    proxyUrl?: string,
    externalSignal?: AbortSignal,
  ): Promise<RuntimeManifest | null> {
    const cacheKey = `${siteKey}:${level}:${pageLocation().pathname}`;
    if (manifestCache.has(cacheKey)) return manifestCache.get(cacheKey)!;

    const base = proxyUrl || 'https://api.goaccessify.de';
    const loc = pageLocation();
    const pageUrl = encodeURIComponent(loc.origin + loc.pathname);
    const url = `${base}/v1/manifest?siteKey=${siteKey}&url=${pageUrl}&feature=simplify&variant=${level}`;

    // Combine timeout + optional external abort signal so a slow/hung Worker
    // cannot freeze the simplify flow and toggle-off stays responsive.
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), MANIFEST_FETCH_TIMEOUT_MS);
    const externalAbortHandler = () => timeoutController.abort();
    if (externalSignal) {
      if (externalSignal.aborted) timeoutController.abort();
      else externalSignal.addEventListener('abort', externalAbortHandler);
    }

    try {
      const res = await fetch(url, { cache: 'no-cache', signal: timeoutController.signal });
      if (!res.ok) { manifestCache.set(cacheKey, null); return null; }
      const data = await res.json() as RuntimeManifest & { miss?: boolean };
      // Always update mode from server — this is the authoritative signal that
      // tells the widget whether live AI fallback is permitted for this site.
      currentSiteMode = data.siteMode === 'auto' ? 'auto' : 'manual';
      telemetryEnabled = !!data.telemetryEnabled;
      // Persist the authoritative mode for the next session so a worker outage
      // on the FIRST page-load does not silently downgrade auto-mode sites.
      persistSiteMode(siteKey, currentSiteMode);
      const result = data.blocks?.length ? data : null;
      manifestCache.set(cacheKey, result);
      return result;
    } catch (err) {
      // Timeouts, aborts, network errors — all fall through to "no manifest".
      // We DO NOT cache aborts/timeouts so the next run can retry, and we keep
      // the previously cached `currentSiteMode` (seeded at construction time
      // from localStorage) so an auto-mode site stays in auto mode across a
      // transient worker outage. DEBUG flag is read inline here because the
      // outer `simplifyPage`'s DEBUG local is not in scope for fetchManifest.
      if ((window as any).__ACCESSIFY_DEBUG) {
        console.warn('[Accessify] Manifest fetch failed:', err);
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
      if (externalSignal) externalSignal.removeEventListener('abort', externalAbortHandler);
    }
  }

  // -------------------------------------------------------------------------
  // Apply manifest blocks to the page (safe replacement)
  // -------------------------------------------------------------------------

  function applyManifestBlocks(
    manifest: RuntimeManifest,
    elements: HTMLElement[],
  ): { applied: HTMLElement[]; remaining: HTMLElement[]; stale: HTMLElement[] } {
    const applied: HTMLElement[] = [];
    const remaining: HTMLElement[] = [];
    const stale: HTMLElement[] = [];

    // Build lookups by block hash AND by CSS selector for fuzzy matching
    const byHash = new Map<string, ManifestBlock>();
    const bySelector = new Map<string, ManifestBlock>();
    for (const block of manifest.blocks) {
      byHash.set(block.blockHash, block);
      if (block.selector) {
        bySelector.set(block.selector, block);
      }
    }

    // Track which manifest blocks have been consumed (applied or claimed as stale).
    // Needed for the Phase C similarity fallback so we don't assign one block to
    // two elements.
    const claimedBlockHashes = new Set<string>();

    for (const el of elements) {
      const text = el.dataset.accessifyOriginal || el.textContent?.trim() || '';
      const elHash = hashText(text);

      // --- Phase A: Exact hash match (text unchanged) ---
      const exactMatch = byHash.get(elHash);
      if (exactMatch?.result) {
        // Pass-through short-circuit: if the manifest result is identical to
        // the current text (contact lines, short headings, addresses — server
        // marks them as pass-through with result = original_text), DO NOT
        // run safeReplace. safeReplace distributes the new text across the
        // element's leaf text nodes proportional to their lengths; when
        // result == original that's mathematically a no-op, BUT the rebuild
        // also clobbers any inline structure (<br>, <a>) the element has —
        // and after 3-4 toggles the leaf-text-node identities drift and the
        // dataset.accessifyOriginal ends up holding HTML-encoded innerHTML
        // (observed on rhinos impressum Mobil/E-Mail block: a fourth toggle
        // produced visible `&lt;br&gt;` / `&lt;a&gt;` text in the DOM).
        if (exactMatch.result === text) {
          applied.push(el);
          claimedBlockHashes.add(exactMatch.blockHash);
          // Stamp the attributes that downstream code expects so the block
          // counts as "applied" without any DOM mutation.
          if (!el.dataset.accessifyOriginal) el.dataset.accessifyOriginal = text;
          el.setAttribute(SIMPLIFIED_ATTR, 'true');
          el.setAttribute(BLOCK_HASH_ATTR, exactMatch.blockHash);
          if (dx.DIAG()) dx.block({ hash: elHash, text: text.slice(0, 60), tag: el.tagName, manifestMatch: 'exact', replaced: true, source: 'manifest', skipReason: 'pass-through-noop' });
          continue;
        }
        const success = safeReplace(el, exactMatch.result, elHash);
        if (success) {
          applied.push(el);
          claimedBlockHashes.add(exactMatch.blockHash);
          // --- DIAG ---
          if (dx.DIAG()) dx.block({ hash: elHash, text: text.slice(0, 60), tag: el.tagName, manifestMatch: 'exact', replaced: true, source: 'manifest' });
        } else {
          remaining.push(el);
          if (dx.DIAG()) dx.block({ hash: elHash, text: text.slice(0, 60), tag: el.tagName, manifestMatch: 'exact', replaced: false, skipReason: 'layout-unsafe', source: 'skipped' });
        }
        continue;
      }

      // --- Phase B: Selector-based fuzzy match (text may have changed slightly) ---
      // Try to find this element in the manifest by CSS selector
      let fuzzyMatch: ManifestBlock | undefined;
      for (const [selector, block] of bySelector) {
        try {
          if (el.matches(selector) || el.closest(selector) === el) {
            fuzzyMatch = block;
            break;
          }
        } catch { /* invalid selector, skip */ }
      }

      if (fuzzyMatch?.result && fuzzyMatch.originalText) {
        const diff = charDiff(text, fuzzyMatch.originalText);
        if (diff <= STALENESS_THRESHOLD) {
          // Minor change (typo fix, date update) — cached simplification still valid
          const success = safeReplace(el, fuzzyMatch.result, elHash);
          if (success) {
            applied.push(el);
            claimedBlockHashes.add(fuzzyMatch.blockHash);
            if (dx.DIAG()) dx.block({ hash: elHash, text: text.slice(0, 60), tag: el.tagName, manifestMatch: 'fuzzy', replaced: true, source: 'manifest' });
          } else {
            remaining.push(el);
            if (dx.DIAG()) dx.block({ hash: elHash, text: text.slice(0, 60), tag: el.tagName, manifestMatch: 'fuzzy', replaced: false, skipReason: 'layout-unsafe', source: 'skipped' });
          }
        } else {
          // Text changed significantly — simplification is stale, needs AI refresh
          console.info(
            `[Accessify] Stale block (diff=${diff}):`,
            text.slice(0, 50) + '…',
          );
          stale.push(el);
          claimedBlockHashes.add(fuzzyMatch.blockHash);
          if (dx.DIAG()) dx.block({ hash: elHash, text: text.slice(0, 60), tag: el.tagName, manifestMatch: 'fuzzy', stale: true, skipReason: `stale(diff=${diff})`, source: 'skipped' });
        }
        continue;
      }

      // No match in Phase A or B — defer to Phase C post-pass (token similarity).
      remaining.push(el);
    }

    // --- Phase C: Token-similarity stale detection ---
    // For each remaining element, check if it's the same block as an unclaimed
    // manifest block (high token-Jaccard overlap) whose originalText has
    // drifted beyond the char-diff threshold. This covers two important cases:
    //   1. Legacy b-xxx selectors from pre-Batch-1 auto-persist that never
    //      matched valid CSS and so always fell through Phase B.
    //   2. Framer/Webflow-generated selectors that only worked on the crawler's
    //      DOM snapshot and not on the rendered page.
    // Without this pass, any block whose content changes meaningfully AND whose
    // selector fails to match becomes permanently invisible to the widget.
    const unclaimedBlocks = manifest.blocks.filter(b =>
      b.originalText && b.result && !claimedBlockHashes.has(b.blockHash),
    );
    if (unclaimedBlocks.length > 0 && remaining.length > 0) {
      const blockTokens = unclaimedBlocks.map(b => ({
        block: b,
        tokens: tokenize(b.originalText!),
      }));

      interface Candidate { el: HTMLElement; block: ManifestBlock; sim: number; }
      const candidates: Candidate[] = [];
      for (const el of remaining) {
        const text = el.dataset.accessifyOriginal || el.textContent?.trim() || '';
        if (text.length < 20) continue;
        const elTokens = tokenize(text);
        if (elTokens.size < 3) continue;  // too short to match meaningfully
        let best: Candidate | null = null;
        for (const { block, tokens } of blockTokens) {
          const sim = tokenSimilarity(elTokens, tokens);
          if (sim >= STALE_SIMILARITY_THRESHOLD && (!best || sim > best.sim)) {
            best = { el, block, sim };
          }
        }
        if (best) candidates.push(best);
      }

      // Greedy assignment: highest similarity first, one block per element.
      candidates.sort((a, b) => b.sim - a.sim);
      const assignedBlocks = new Set<string>();
      const assignedEls = new Set<HTMLElement>();
      for (const c of candidates) {
        if (assignedBlocks.has(c.block.blockHash)) continue;
        if (assignedEls.has(c.el)) continue;
        assignedBlocks.add(c.block.blockHash);
        assignedEls.add(c.el);
        stale.push(c.el);
        claimedBlockHashes.add(c.block.blockHash);
        const elText = c.el.dataset.accessifyOriginal || c.el.textContent?.trim() || '';
        console.info(
          `[Accessify] Stale block (similarity=${c.sim.toFixed(2)}):`,
          elText.slice(0, 50) + '…',
        );
        if (dx.DIAG()) dx.block({
          hash: hashText(elText),
          text: elText.slice(0, 60),
          tag: c.el.tagName,
          manifestMatch: 'similarity',
          stale: true,
          skipReason: `stale(sim=${c.sim.toFixed(2)})`,
          source: 'skipped',
        });
      }

      // Rebuild remaining without the newly-claimed stale elements.
      if (assignedEls.size > 0) {
        const stillRemaining = remaining.filter(el => !assignedEls.has(el));
        remaining.length = 0;
        remaining.push(...stillRemaining);
      }
    }

    return { applied, remaining, stale };
  }

  // -------------------------------------------------------------------------
  // Live fallback auto-persist — write back to D1+KV
  // -------------------------------------------------------------------------

  // R8-3: jsdom (the vitest test environment) does not implement
  // `CSS.escape`, so any unit test that hits buildSelector explodes with
  // a TypeError. Browsers have always shipped it. Falls back to a
  // permissive identifier-escape that's sufficient for the IDs we
  // actually persist (alphanumerics + hyphen/underscore are normal).
  const cssEscape = (s: string): string => {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(s);
    }
    return s.replace(/[^a-zA-Z0-9_\-]/g, (ch) => `\\${ch}`);
  };

  /** Build a minimal CSS selector for a DOM element so the server can store it */
  function buildSelector(el: HTMLElement): string {
    const tag = el.tagName.toLowerCase();
    // If element has an id, use that (most specific)
    if (el.id) return `#${cssEscape(el.id)}`;
    // Otherwise use nth-of-type within parent
    const parent = el.parentElement;
    if (!parent) return tag;
    const siblings = Array.from(parent.children).filter(c => c.tagName === el.tagName);
    if (siblings.length === 1) return tag;
    const idx = siblings.indexOf(el) + 1;
    return `${tag}:nth-of-type(${idx})`;
  }

  /** Batch buffer: collect blocks, flush once after simplification completes */
  const persistQueue: Array<{
    blockHash: string;
    originalText: string;
    result: string;
    selector: string;
  }> = [];
  let persistFlushTimer: ReturnType<typeof setTimeout> | null = null;
  const persistedHashes = new Set<string>(); // deduplicate across toggle cycles

  function persistToManifest(
    siteKey: string,
    blockHash: string,
    originalText: string,
    simplifiedText: string,
    selector: string,
    proxyUrl?: string,
  ): void {
    // Skip if already persisted this session
    if (persistedHashes.has(blockHash)) return;
    persistedHashes.add(blockHash);

    persistQueue.push({ blockHash, originalText, result: simplifiedText, selector });

    // Debounce: flush 2s after last persist call (batches rapid-fire calls)
    if (persistFlushTimer) clearTimeout(persistFlushTimer);
    persistFlushTimer = setTimeout(() => flushPersistQueue(siteKey, proxyUrl), 2000);
  }

  function flushPersistQueue(siteKey: string, proxyUrl?: string): void {
    if (persistQueue.length === 0) return;
    // Always clear any pending debounce timer — regardless of who triggered
    // the flush (debounce fire, deactivate, pagehide). Prevents double-flushes.
    if (persistFlushTimer) { clearTimeout(persistFlushTimer); persistFlushTimer = null; }

    const base = proxyUrl || 'https://api.goaccessify.de';
    const { origin, pathname } = pageLocation();
    const pageUrl = origin + pathname;
    const blocks = persistQueue.splice(0);

    // Telemetry: one "attempt" regardless of how many blocks the batch contains
    // (the unit a pilot dashboard cares about is "did the write-back POST reach
    // the server"). We increment by blocks.length so the ratio reflects
    // individual blocks, which is what AI-Savings math consumes downstream.
    const telemetrySnapshot = tel.run;
    if (telemetrySnapshot) telemetrySnapshot.persistAttempts += blocks.length;

    fetch(`${base}/v1/cache/persist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Site-Key': siteKey,
      },
      body: JSON.stringify({
        siteKey,
        url: pageUrl,
        feature: 'simplify',
        variant: level,
        blocks,
      }),
    }).then((res) => {
      if (res.ok) {
        if (telemetrySnapshot) telemetrySnapshot.persistSuccesses += blocks.length;
        if ((window as any).__ACCESSIFY_DEBUG) {
          console.log(`[Accessify] Persisted ${blocks.length} blocks to D1+KV`);
        }
        // Invalidate the in-memory manifest cache for this page so that the
        // next fetchManifest() call re-reads the (now-fresh) KV manifest
        // instead of serving the pre-persist snapshot. Without this, same-
        // session re-activation (toggle off → on on the same URL) would hit
        // the cached old manifest and wastefully re-trigger AI for blocks
        // the server already knows about.
        invalidateManifestCacheForCurrentPage(siteKey);
        // --- DIAG: mark persisted blocks ---
        if (dx.DIAG() && dx.run) {
          for (const b of blocks) {
            const entry = dx.run.blocks.find(d => d.hash === b.blockHash);
            if (entry) entry.persistOk = true;
          }
          console.log(
            `%c[Accessify DIAG] Persist OK: ${blocks.length} blocks → ${pageUrl}`,
            'color: #22c55e',
          );
        }
      } else {
        console.warn(`[Accessify] Persist failed: ${res.status}`);
        // --- DIAG: mark persist failure ---
        if (dx.DIAG() && dx.run) {
          for (const b of blocks) {
            const entry = dx.run.blocks.find(d => d.hash === b.blockHash);
            if (entry) entry.persistOk = false;
          }
          console.warn(
            `%c[Accessify DIAG] Persist FAILED (${res.status}): ${blocks.length} blocks`,
            'color: #ef4444',
          );
        }
      }
    }).catch((err) => {
      console.warn('[Accessify] Failed to persist live simplification:', err);
      // --- DIAG: mark persist error ---
      if (dx.DIAG() && dx.run) {
        for (const b of blocks) {
          const entry = dx.run.blocks.find(d => d.hash === b.blockHash);
          if (entry) entry.persistOk = false;
        }
      }
    });
  }

  // -------------------------------------------------------------------------
  // Loading overlay on individual elements
  // -------------------------------------------------------------------------

  function markLoading(el: HTMLElement) {
    el.style.opacity = '0.5';
    el.style.transition = 'opacity 0.3s ease';
    // Stable selector hook for tests + external integrations (R6-3).
    // Anyone watching for "is this paragraph waiting on AI?" can query
    // [data-accessify-loading="true"] instead of inspecting opacity.
    el.setAttribute('data-accessify-loading', 'true');
  }

  function clearLoading(el: HTMLElement) {
    el.style.opacity = '';
    el.style.transition = '';
    el.removeAttribute('data-accessify-loading');
  }

  /**
   * Mark a paragraph as failed (R6-3): visible flag + tooltip with the
   * reason. The replacement attempt is over; the original text stays in
   * place so the user can still read it.
   */
  function markError(el: HTMLElement, reason: string) {
    clearLoading(el);
    el.setAttribute('data-accessify-error', 'true');
    el.setAttribute('title', reason);
    el.style.textDecoration = 'underline dotted rgba(239,68,68,0.5)';
    el.style.textUnderlineOffset = '3px';
  }

  function clearError(el: HTMLElement) {
    el.removeAttribute('data-accessify-error');
    el.style.textDecoration = '';
    el.style.textUnderlineOffset = '';
  }

  // -------------------------------------------------------------------------
  // Global progress indicator
  // -------------------------------------------------------------------------

  function showProgress(_current: number, _total: number, _skipped: number = 0) {
    // Progress overlay disabled — text appears silently as it's ready
  }

  function showDone(_count: number, _fromCache: number) {
    // Silently remove progress overlay — no "done" banner needed
    removeProgress();
  }

  function showError(msg: string) {
    if (overlayEl) {
      // Build via textContent so any provider/network error text is never
      // interpreted as HTML — prevents script injection via error messages.
      overlayEl.textContent = '';
      const icon = document.createElement('span');
      icon.style.color = '#ef4444';
      icon.textContent = '\u2715 ';
      overlayEl.appendChild(icon);
      overlayEl.appendChild(document.createTextNode(String(msg ?? '')));
      overlayEl.style.color = '#ef4444';
      setTimeout(() => removeProgress(), 5000);
    }
  }

  function removeProgress() {
    overlayEl?.remove();
    overlayEl = null;
    document.getElementById('accessify-autospin-style')?.remove();
  }

  // -------------------------------------------------------------------------
  // AI Disclaimer Banner — shown on the actual page (outside shadow DOM)
  // -------------------------------------------------------------------------
  const DISCLAIMER_ID = 'accessify-ai-disclaimer';

  function showDisclaimer() {
    if (document.getElementById(DISCLAIMER_ID)) return;
    const text = lang.startsWith('de')
      ? 'KI-vereinfachter Text'
      : 'AI-simplified text';

    const banner = document.createElement('div');
    banner.id = DISCLAIMER_ID;
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    banner.textContent = text;
    Object.assign(banner.style, {
      position: 'fixed',
      bottom: '12px',
      left: '12px',
      zIndex: '2147483640',
      padding: '6px 12px',
      background: 'rgba(30,41,59,0.85)',
      color: '#94a3b8',
      fontSize: '11px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      borderRadius: '6px',
      pointerEvents: 'none',
    });
    document.body.appendChild(banner);
  }

  function removeDisclaimer() {
    document.getElementById(DISCLAIMER_ID)?.remove();
  }

  // -------------------------------------------------------------------------
  // Core: simplify page — manifest first, live fallback for misses
  // -------------------------------------------------------------------------

  async function simplifyPage() {
    const elements = getTextElements();
    if (elements.length === 0) return;

    // Abort any still-running previous run, then start a new one.
    // We hold a LOCAL reference (runController/runSignal) for this run so
    // post-await checks stay correct even if `abortController` is nulled
    // mid-flight by deactivate(). isAborted() also OR's against `!enabled`
    // so a deactivate that races past abort() still short-circuits the run.
    abortController?.abort();
    const runController = new AbortController();
    abortController = runController;
    const runSignal = runController.signal;
    const isAborted = () => runSignal.aborted || !enabled;

    savedParagraphs = [];
    skippedBlocks = 0;

    // Fresh pilot-telemetry counters for this run.
    tel.start(elements.length);

    // Set up observers
    setupResizeObserver();
    setupMutationObserver();

    const { siteKey, proxyUrl } = getConfig();
    let fromCache = 0;
    let remaining = elements;
    // Track stale blocks — these MUST be re-generated by AI; if AI fails, skip them
    const staleBlocks = new Set<HTMLElement>();
    // Track manifest-applied elements — used by the Safety Gate to build the
    // content-anchor set that distinguishes real content from boilerplate.
    let appliedFromManifest: HTMLElement[] = [];

    const DEBUG = !!(window as any).__ACCESSIFY_DEBUG;

    // Step-by-step diagnostics gated behind window.__ACCESSIFY_DEBUG = true.
    // Lets an audit observer pinpoint exactly which branch short-circuits a
    // simplify run before Phase 2 reaches an AI call — R7-1's verification
    // hook. The tracer reports state right before every early-return point.
    const trace = (where: string, extra: Record<string, unknown> = {}) => {
      if (!DEBUG) return;
      console.info('[Accessify-DEBUG] simplifyPage', where, {
        elementsCount: elements.length,
        siteKey: siteKey ? 'set' : 'missing',
        proxyUrl: proxyUrl || '(none)',
        currentSiteMode,
        aiServiceReady: !!aiService,
        fromCache,
        remainingCount: Array.isArray(remaining) ? remaining.length : 'n/a',
        ...extra,
      });
    };
    trace('start');

    // --- DIAG: init run ---
    if (dx.DIAG()) dx.init(window.location.pathname, siteKey || null);
    if (dx.run) dx.run.elementsFound = elements.length;

    // -- Phase 1: Try manifest (instant, no AI calls) -------------------------
    if (siteKey) {
      showProgress(0, elements.length);
      const manifestStartedAt = Date.now();
      const manifest = await fetchManifest(siteKey, proxyUrl, runSignal);
      if (tel.run) tel.run.manifestLatencyMs = Date.now() - manifestStartedAt;

      if (DEBUG) console.log(`[Accessify] Manifest: ${manifest?.blocks?.length ?? 0} blocks for ${elements.length} elements`);
      // --- DIAG: manifest result ---
      if (dx.run) {
        dx.run.manifestLoaded = !!manifest?.blocks?.length;
        dx.run.manifestBlocks = manifest?.blocks?.length ?? 0;
        dx.run.siteMode = currentSiteMode;
      }
      if (tel.run) {
        tel.run.manifestHit = !!manifest?.blocks?.length;
        tel.run.manifestBlocks = manifest?.blocks?.length ?? 0;
      }

      if (isAborted()) { if (dx.DIAG()) dx.finish(); return; }

      if (manifest?.blocks?.length) {
        // Server manifest is authoritative — clear old client cache to prevent
        // stale hallucinations from previous live AI runs overriding server data
        await idb.clearForCurrentPage();
        if (isAborted()) { if (dx.DIAG()) dx.finish(); return; }

        const result = applyManifestBlocks(manifest, elements);
        fromCache = result.applied.length;
        appliedFromManifest = result.applied;
        if (DEBUG) console.log(`[Accessify] Manifest hit: ${fromCache}, stale: ${result.stale.length}, remaining: ${result.remaining.length}`);
        // Stale blocks get prepended to remaining so AI handles them
        for (const el of result.stale) staleBlocks.add(el);
        remaining = [...result.stale, ...result.remaining];

        // Populate client cache with (correct) manifest results
        for (const block of manifest.blocks) {
          if (block.result) {
            idb.set(idb.key(block.blockHash), block.result);
          }
        }

        // --- DIAG: Phase 1 counters ---
        if (dx.run) {
          dx.run.phase1Applied = result.applied.length;
          dx.run.phase1Stale = result.stale.length;
        }
        if (tel.run) {
          tel.run.phase1Applied = result.applied.length;
          tel.run.phase1Stale = result.stale.length;
        }

        if (remaining.length === 0) {
          // All blocks were in the manifest — done instantly
          trace('return: all-blocks-from-manifest');
          showDone(fromCache, fromCache);
          if (dx.DIAG()) dx.finish();
          return;
        }
      }
    }
    trace('after-phase1');

    // -- Phase 1.5: Check client-side IndexedDB cache --------------------------
    {
      const stillRemaining: HTMLElement[] = [];
      for (const el of remaining) {
        if (isAborted()) return;
        const text = el.dataset.accessifyOriginal || el.textContent?.trim() || '';
        if (text.length < 20) { stillRemaining.push(el); continue; }
        const blockHash = hashText(text);
        const cached = await idb.get(idb.key(blockHash));
        if (isAborted()) return;
        if (cached) {
          const success = safeReplace(el, cached, blockHash);
          if (success) {
            fromCache++;
            if (tel.run) tel.run.phase15IdbHits++;
            // --- DIAG ---
            if (dx.DIAG()) {
              dx.block({ hash: blockHash, text: text.slice(0, 60), tag: el.tagName, idbHit: true, replaced: true, source: 'idb' });
              if (dx.run) dx.run.phase15IdbHits++;
            }
          } else {
            stillRemaining.push(el);
            if (dx.DIAG()) dx.block({ hash: blockHash, text: text.slice(0, 60), tag: el.tagName, idbHit: true, replaced: false, skipReason: 'layout-unsafe', source: 'skipped' });
          }
        } else {
          stillRemaining.push(el);
          // (no diag entry yet — will be created in Phase 2 or safety gate)
        }
      }
      remaining = stillRemaining;

      if (remaining.length === 0) {
        trace('return: all-blocks-from-idb-cache');
        showDone(fromCache, fromCache);
        if (dx.DIAG()) dx.finish();
        return;
      }
    }
    trace('after-phase1.5-idb');

    // -- Phase 2: Live fallback for remaining blocks --------------------------
    // SAFETY GATE: Decide which of the still-unmatched elements should go to
    // live AI versus be skipped as boilerplate.
    //
    // The old rule was "manifest loaded + element not in manifest → skip as
    // nav/footer/sidebar". That collapsed two different populations into one:
    //   (a) truly structural boilerplate (nav, menu, legal footer) that the
    //       crawler correctly excluded — should stay skipped
    //   (b) content that drifted away from the manifest (new paragraph,
    //       rewritten block, Framer layout change) — should go to AI
    // The content-anchor heuristic separates them: ancestors of applied
    // manifest blocks form a "content region" set. Remaining elements that
    // sit inside that set are near real content and treated as candidates
    // for live AI. Elements outside any content region plus elements inside
    // obvious navigation landmarks stay skipped.
    //
    // Without a manifest, the old behaviour applies:
    //   manual mode → skip all live AI
    //   auto mode   → live AI for everything (first visit before any crawl)
    const manifestWasLoaded = fromCache > 0 || (siteKey && staleBlocks.size > 0);

    if (manifestWasLoaded && remaining.length > 0) {
      const skipAsBoilerplate: HTMLElement[] = [];
      const keepForAI: HTMLElement[] = [];

      for (const el of remaining) {
        // Stale blocks always go to AI — their content is known to have drifted.
        if (staleBlocks.has(el)) { keepForAI.push(el); continue; }
        // Obvious navigation landmarks always skip, even if near content.
        if (isObviousBoilerplate(el)) { skipAsBoilerplate.push(el); continue; }
        // Mode-specific gating:
        //   auto mode  → keep every non-obvious-boilerplate block for AI.
        //                The anchor used to gate this, but that broke
        //                multi-section pages where only one content region
        //                got matched in the manifest: blocks in OTHER content
        //                regions (a second column, a separate <main>) were
        //                wrongly classified as boilerplate because no applied
        //                ancestor existed near them. Anchor is still computed
        //                so DIAG/telemetry can attribute the decision.
        //   manual mode → leftover blocks stay skipped; live AI is forbidden
        //                 in manual mode regardless of proximity, and the
        //                 manifest is treated as authoritative.
        if (currentSiteMode === 'auto') {
          keepForAI.push(el);
        } else {
          skipAsBoilerplate.push(el);
        }
      }

      if (skipAsBoilerplate.length > 0 && DEBUG) {
        console.info(
          `[Accessify] Safety Gate: ${skipAsBoilerplate.length} boilerplate skipped, ` +
          `${keepForAI.length} queued for AI (stale + near-content).`,
        );
      }

      // --- DIAG: log skipped boilerplate ---
      if (dx.DIAG()) {
        for (const el of skipAsBoilerplate) {
          const t = el.dataset.accessifyOriginal || el.textContent?.trim() || '';
          const reason = isObviousBoilerplate(el)
            ? 'structural-boilerplate(nav/menu)'
            : currentSiteMode === 'auto'
              ? 'no-content-anchor'
              : `manual-mode(siteMode=${currentSiteMode})`;
          dx.block({ hash: hashText(t), text: t.slice(0, 60), tag: el.tagName, skipReason: reason, source: 'skipped' });
        }
      }

      // Clear loading on skipped blocks
      for (const el of skipAsBoilerplate) clearLoading(el);
      remaining = keepForAI;

      if (remaining.length === 0) {
        // All manifest blocks applied, no stale or near-content leftovers — done
        showDone(fromCache, fromCache);
        showDisclaimer();
        if (dx.DIAG()) dx.finish();
        return;
      }
    } else if (currentSiteMode !== 'auto' && remaining.length > 0) {
      // No manifest — manual mode: skip all live AI
      trace('return: manual-mode-no-manifest', { remaining: remaining.length });
      if (DEBUG) {
        console.info(
          `[Accessify] siteMode=${currentSiteMode} — skipping live AI for ${remaining.length} uncached block(s). ` +
          `Site owner: trigger a crawl from the dashboard or switch to 'auto' mode.`,
        );
      }
      // --- DIAG: log all blocks skipped by manual mode ---
      if (dx.DIAG()) {
        for (const el of remaining) {
          const t = el.dataset.accessifyOriginal || el.textContent?.trim() || '';
          dx.block({ hash: hashText(t), text: t.slice(0, 60), tag: el.tagName, skipReason: `manual-mode(siteMode=${currentSiteMode})`, source: 'skipped' });
        }
      }
      if (staleBlocks.size > 0) skippedBlocks += staleBlocks.size;
      // Clear loading state on blocks we won't touch
      for (const el of remaining) clearLoading(el);
      if (fromCache > 0) {
        showDone(fromCache, fromCache);
        showDisclaimer();
      } else {
        removeProgress();
      }
      if (dx.DIAG()) dx.finish();
      return;
    }

    trace('before-phase2-ai');
    // If no AI service available: stale blocks must be skipped (never show outdated text),
    // non-stale remaining blocks also can't be simplified → show appropriate message
    if (!aiService && remaining.length > 0) {
      trace('return: no-ai-service', { remaining: remaining.length });
      // Skip all stale blocks — outdated simplifications must NOT be shown
      if (staleBlocks.size > 0) {
        skippedBlocks += staleBlocks.size;
        console.info(`[Accessify] ${staleBlocks.size} stale block(s) skipped — no AI service to regenerate`);
      }
      if (fromCache > 0) {
        showDone(fromCache, fromCache);
      } else {
        showProgress(0, 1);
        showError(lang.startsWith('de')
          ? 'Kein API-Key konfiguriert'
          : 'No API key configured');
      }
      return;
    }

    // Mark remaining elements as loading
    for (const el of remaining) {
      markLoading(el);
    }

    const totalElements = elements.length;
    let completed = fromCache;
    let totalSimplified = fromCache;
    const isStale = (el: HTMLElement) => staleBlocks.has(el);

    showProgress(completed, totalElements, skippedBlocks);

    for (const el of remaining) {
      if (isAborted()) break;

      const text = el.dataset.accessifyOriginal || el.textContent?.trim() || '';
      if (text.length < 20) {
        clearLoading(el);
        completed++;
        showProgress(completed, totalElements, skippedBlocks);
        continue;
      }

      // Skip short credit/attribution lines in live AI (hallucination risk)
      if (text.length < 60 && CREDIT_LINE_PATTERN.test(text)) {
        clearLoading(el);
        completed++;
        skippedBlocks++;
        showProgress(completed, totalElements, skippedBlocks);
        continue;
      }

      const blockHash = hashText(text);
      if (tel.run) tel.run.phase2AiCalls++;
      // --- DIAG: track this AI block ---
      let diagEntry: DiagBlock | null = null;
      if (dx.DIAG()) {
        diagEntry = dx.block({ hash: blockHash, text: text.slice(0, 60), tag: el.tagName, liveAI: true, stale: isStale(el), source: 'ai' });
        if (dx.run) dx.run.phase2AICalls++;
      }

      try {
        const simplified = await aiService!.simplifyText(text, level, lang);
        if (isAborted()) return;

        if (simplified && simplified !== text) {
          const success = safeReplace(el, simplified, blockHash);
          if (success) {
            totalSimplified++;

            // Cache client-side for instant reload
            idb.set(idb.key(blockHash), simplified);

            // Auto-persist live result back to manifest (non-blocking)
            if (siteKey) {
              persistToManifest(siteKey, blockHash, text, simplified, buildSelector(el), proxyUrl);
            }
            if (diagEntry) diagEntry.replaced = true;
          } else {
            if (diagEntry) { diagEntry.replaced = false; diagEntry.skipReason = 'layout-unsafe'; }
          }
        } else {
          if (diagEntry) { diagEntry.replaced = false; diagEntry.skipReason = simplified ? 'ai-returned-identical' : 'ai-returned-empty'; }
        }
      } catch (err: any) {
        if (diagEntry) { diagEntry.skipReason = `error:${err?.message?.slice(0, 50) || 'unknown'}`; diagEntry.replaced = false; }
        if (dx.run) dx.run.phase2AIErrors++;
        if (tel.run) tel.run.phase2AiErrors++;
        // Visible per-block error marker (R6-3) — replaces the old silent
        // failure. The user still sees the original paragraph; the dotted
        // red underline + title attribute reveal that the AI step failed.
        const errMsg = lang.startsWith('de')
          ? 'Vereinfachen fehlgeschlagen'
          : 'Simplification failed';
        markError(el, errMsg);
        // Abort immediately on auth errors — no point retrying
        if (err?.message?.includes('401') || err?.message?.includes('403')) {
          console.warn('[Accessify] AI auth failed, stopping live simplification');
          // Stale blocks that haven't been processed yet must be counted as skipped
          for (const r of remaining) {
            if (isStale(r) && !r.hasAttribute(SIMPLIFIED_ATTR)) skippedBlocks++;
          }
          clearLoading(el);
          completed++;
          break;
        }
        if (err?.message?.includes('429')) {
          // Rate limited — retry once after delay
          await new Promise((r) => setTimeout(r, 8000));
          if (isAborted()) return;
          try {
            const retry = await aiService!.simplifyText(text, level, lang);
            if (isAborted()) return;
            if (retry && retry !== text) {
              const success = safeReplace(el, retry, blockHash);
              if (success) {
                totalSimplified++;
                idb.set(idb.key(blockHash), retry);
                if (siteKey) {
                  persistToManifest(siteKey, blockHash, text, retry, buildSelector(el), proxyUrl);
                }
                if (diagEntry) { diagEntry.replaced = true; diagEntry.skipReason = null; }
              }
            }
          } catch {
            // Stale block that failed AI regeneration → skip it entirely
            if (isStale(el)) {
              skippedBlocks++;
              console.warn('[Accessify] Stale block skipped — AI retry failed:', text.slice(0, 50));
            }
          }
        } else {
          // Any other AI error on a stale block → skip (don't show outdated text)
          if (isStale(el)) {
            skippedBlocks++;
            console.warn('[Accessify] Stale block skipped — AI error:', text.slice(0, 50));
          } else {
            console.warn('[Accessify] Failed to simplify:', err);
          }
        }
      } finally {
        clearLoading(el);
        completed++;
        if (!isAborted()) {
          showProgress(completed, totalElements, skippedBlocks);
          // Throttle: small delay between requests to respect free-tier rate limits
          await new Promise((r) => setTimeout(r, 600));
        }
      }
      // After the throttle sleep, check abort again before continuing the loop.
      if (isAborted()) break;
    }

    // Clean up any remaining loading indicators (e.g. after auth-error break)
    for (const el of remaining) {
      clearLoading(el);
    }

    if (!isAborted()) {
      showDone(totalSimplified, fromCache);
      // Show AI disclaimer on the page
      if (totalSimplified > 0 || fromCache > 0) {
        showDisclaimer();
      }
    }
    // --- DIAG: finalize run ---
    if (dx.DIAG()) dx.finish();
  }

  // -------------------------------------------------------------------------
  // Restore original text — clean teardown
  // -------------------------------------------------------------------------

  function restorePage() {
    // Disconnect observers first to avoid triggering them during restore
    disconnectObservers();

    const restoreReason = abortController?.signal.aborted ? 'abort(navigation/toggle)' : 'toggle-off';

    // Defensive sweep: if a late write slipped past every abort guard (e.g. a
    // racing AI response that resolved between our check and safeReplace), the
    // element would carry SIMPLIFIED_ATTR but not appear in savedParagraphs.
    // Scan the DOM once and fold any such orphan into the restore list before
    // the main loop runs.
    const trackedEls = new Set(savedParagraphs.map((s) => s.el));
    document.querySelectorAll(`[${SIMPLIFIED_ATTR}]`).forEach((node) => {
      const el = node as HTMLElement;
      if (trackedEls.has(el)) return;
      // Best-effort: use the saved original text if we have it. We prefer
      // textContent over innerHTML here because orphans have no savedTextNodes
      // to work with and innerHTML would need the pre-replace markup we never
      // stored for this element.
      const originalText = el.dataset.accessifyOriginal;
      if (originalText && el.textContent !== originalText) {
        el.textContent = originalText;
      }
      el.removeAttribute(SIMPLIFIED_ATTR);
      el.removeAttribute(ORIGINAL_ATTR);
      el.removeAttribute(BLOCK_HASH_ATTR);
      el.removeAttribute('data-accessify-replacing');
      clearLoading(el);
      if (el.dataset.accessifyOrigTransform) {
        el.style.textTransform = el.dataset.accessifyOrigTransform;
        delete el.dataset.accessifyOrigTransform;
      }
      if (dx.DIAG() && dx.run) {
        dx.run.restoreLog.push({
          ts: new Date().toISOString(),
          hash: el.getAttribute(BLOCK_HASH_ATTR) || 'orphan',
          text: originalText?.slice(0, 60) || '',
          textNodeRestore: false,
          innerHtmlFallback: false,
          reason: `${restoreReason}+orphan-sweep`,
        });
      }
    });

    for (const { el, originalHtml, savedTextNodes, ancestorPatches } of savedParagraphs) {
      const blockHash = el.getAttribute(BLOCK_HASH_ATTR) || 'unknown';
      let textNodeRestore = false;
      let innerHtmlFallback = false;

      // Prefer text-node restore (React/Framer-safe: no DOM structure change)
      if (savedTextNodes.length > 0) {
        let allRestored = true;
        for (const sn of savedTextNodes) {
          if (sn.node.parentNode) {
            sn.node.data = sn.original; // NUR .data setzen!
          } else {
            allRestored = false;
          }
        }
        // Fallback to innerHTML only if text nodes were removed by framework re-render
        if (!allRestored) {
          el.innerHTML = originalHtml;
          innerHtmlFallback = true;
        } else {
          textNodeRestore = true;
        }
      } else {
        el.innerHTML = originalHtml;
        innerHtmlFallback = true;
      }

      // --- DIAG: log restore ---
      if (dx.DIAG() && dx.run) {
        dx.run.restoreLog.push({
          ts: new Date().toISOString(),
          hash: blockHash,
          text: (el.textContent?.trim() || '').slice(0, 60),
          textNodeRestore,
          innerHtmlFallback,
          reason: innerHtmlFallback ? `${restoreReason}+innerHTML-fallback(text-nodes-detached)` : restoreReason,
        });
      }

      el.removeAttribute(ORIGINAL_ATTR);
      el.removeAttribute(SIMPLIFIED_ATTR);
      el.removeAttribute(BLOCK_HASH_ATTR);
      el.removeAttribute('data-accessify-replacing');
      clearLoading(el);

      // Restore original text-transform if we overrode it
      if (el.dataset.accessifyOrigTransform) {
        el.style.textTransform = el.dataset.accessifyOrigTransform;
        delete el.dataset.accessifyOrigTransform;
      }

      // Revert any ancestor patches (max-height, overflow changes)
      revertPatches(ancestorPatches);
    }

    // --- DIAG: log restore summary ---
    if (dx.DIAG() && dx.run) {
      const rl = dx.run.restoreLog;
      console.log(
        `%c[Accessify DIAG] Restore: ${rl.length} blocks, ${rl.filter(r => r.textNodeRestore).length} text-node, ${rl.filter(r => r.innerHtmlFallback).length} innerHTML-fallback`,
        'color: #f59e0b; font-weight: bold',
      );
      if (rl.some(r => r.innerHtmlFallback)) {
        console.warn('[Accessify DIAG] innerHTML fallbacks detected — framework may have detached text nodes:');
        console.table(rl.filter(r => r.innerHtmlFallback));
      }
    }

    // Final sweep: clear any leftover loading/error markers that didn't
    // make it through the savedParagraphs path (e.g. blocks that failed AI
    // and were never replaced still carry the dotted-red underline + title).
    document.querySelectorAll<HTMLElement>('[data-accessify-loading="true"]').forEach((el) => clearLoading(el));
    document.querySelectorAll<HTMLElement>('[data-accessify-error="true"]').forEach((el) => clearError(el));

    savedParagraphs = [];
    skippedBlocks = 0;
  }

  // -------------------------------------------------------------------------
  // Preferences
  // -------------------------------------------------------------------------

  function loadPreferences() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.level === 'einfache' || parsed.level === 'leichte') {
          level = parsed.level;
        }
      }
    } catch { /* use defaults */ }
  }

  function savePreferences() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ level }));
  }

  // -------------------------------------------------------------------------
  // Module lifecycle
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  // SPA navigation detection — re-simplify on URL change
  // -------------------------------------------------------------------------

  let lastUrl = '';
  let navigationCleanup: (() => void) | null = null;

  function setupNavigationWatcher() {
    lastUrl = location.href;

    const onUrlChange = () => {
      if (!enabled || location.href === lastUrl) return;
      lastUrl = location.href;
      if ((window as any).__ACCESSIFY_DEBUG) {
        console.log('[Accessify] URL changed, re-simplifying:', lastUrl);
      }
      // Abort current run, restore old page, re-simplify new content
      abortController?.abort();
      restorePage();
      removeProgress();
      // Small delay for new DOM to settle
      setTimeout(() => { if (enabled) simplifyPage(); }, 500);
    };

    // popstate fires on back/forward
    window.addEventListener('popstate', onUrlChange);

    // Intercept pushState / replaceState for SPA routers
    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);
    history.pushState = function(...args: any[]) {
      origPush(...(args as [any, string, string?]));
      onUrlChange();
    };
    history.replaceState = function(...args: any[]) {
      origReplace(...(args as [any, string, string?]));
      onUrlChange();
    };

    navigationCleanup = () => {
      window.removeEventListener('popstate', onUrlChange);
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }

  function activate() {
    if (enabled) return;
    enabled = true;
    loadPreferences();
    setupNavigationWatcher();
    setupPersistBeacon();
    simplifyPage();
  }

  function deactivate() {
    // ORDER MATTERS: flip `enabled` first so any in-flight isAborted() checks
    // inside simplifyPage short-circuit even if abort() has not yet propagated
    // through the microtask queue. Only THEN abort + null the controller.
    enabled = false;
    abortController?.abort();
    abortController = null;

    // Force-flush any pending persist BEFORE teardown. Without this, toggling
    // off within the 2-second debounce window silently loses every Live-AI
    // result we just produced — breaking the "einmal AI, danach für alle aus
    // KV" promise for the next visitor.
    const { siteKey, proxyUrl } = getConfig();
    if (siteKey && persistQueue.length > 0) {
      flushPersistQueue(siteKey, proxyUrl);
    }

    navigationCleanup?.();
    navigationCleanup = null;
    pagehideCleanup?.();
    pagehideCleanup = null;
    restorePage();
    removeProgress();
    removeDisclaimer();
  }

  /** Cleanup handle for the pagehide beacon listener. */
  let pagehideCleanup: (() => void) | null = null;

  /** Install a pagehide listener that beacons the persist queue AND the
   *  pilot telemetry event before the page unloads. Reliable on mobile
   *  Safari and background-tabbed sessions where regular fetch() is
   *  cancelled as the tab goes away. */
  function setupPersistBeacon() {
    if (pagehideCleanup) return;
    const handler = () => {
      const { siteKey, proxyUrl } = getConfig();
      if (!siteKey) return;
      const base = proxyUrl || 'https://api.goaccessify.de';
      const { origin, pathname } = pageLocation();
      const pageUrl = origin + pathname;

      // ── 1) Persist-queue beacon (unchanged behaviour) ──
      if (persistQueue.length > 0) {
        const blocks = persistQueue.splice(0);
        const body = JSON.stringify({ siteKey, url: pageUrl, feature: 'simplify', variant: level, blocks });
        try {
          const sent = typeof navigator !== 'undefined' && navigator.sendBeacon
            ? navigator.sendBeacon(`${base}/v1/cache/persist`, new Blob([body], { type: 'application/json' }))
            : false;
          if (!sent) {
            fetch(`${base}/v1/cache/persist`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Site-Key': siteKey },
              body,
              keepalive: true,
            }).catch(() => { /* best-effort */ });
          }
        } catch { /* best-effort */ }
      }

      // ── 2) Pilot telemetry beacon (opt-in per site) ──
      // Privacy: numeric counters + a DJB2-hashed pathname only. Implementation
      // lives in text-simplify-telemetry.ts; the call is a no-op if the site
      // hasn't opted into telemetry or there is no current run.
      if (siteKey) tel.beacon({ siteKey, base, variant: level });
    };
    window.addEventListener('pagehide', handler);
    pagehideCleanup = () => window.removeEventListener('pagehide', handler);
  }

  return {
    id: 'text-simplify',
    name: () => (lang.startsWith('de') ? 'Text vereinfachen' : 'Simplify Text'),
    description: lang.startsWith('de')
      ? 'Text in Einfache Sprache umwandeln'
      : 'Simplify text for easier understanding',
    icon: 'simplify-text',
    category: 'ai',
    activate,
    deactivate,
    getState: (): FeatureState => ({
      id: 'text-simplify',
      enabled,
      value: { level } as TextSimplifyState,
    }),
    setState: (newState: Partial<TextSimplifyState>) => {
      if (newState.level && (newState.level === 'einfache' || newState.level === 'leichte')) {
        level = newState.level;
        savePreferences();
      }
    },
  };
}
